import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Mail, Ban, CheckCircle2, Plus } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, DataTable, GhostButton } from "@/components/admin/ui";
import MarkPaidDialog from "@/components/admin/MarkPaidDialog";
import AddStudentAdminDialog from "@/components/admin/AddStudentAdminDialog";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg, formatDateTime } from "@/lib/api";
import { Pill, STUDENT_STATUS, PAYMENT_STATE, CONSENT_STATUS } from "@/lib/status";

export default function ParentDetail() {
  const { id } = useParams();
  const { data, reload } = useFetch(`/admin/parents/${id}`);
  const [paying, setPaying] = useState(null);
  const [adding, setAdding] = useState(false);
  if (!data) return <PageHeader title="Parent" />;
  const p = data.parent;

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="parent-detail-page">
      <Link to="/admin/parents" className="inline-flex items-center gap-2 text-sm text-[#635060] hover:text-[#4F0341] mb-4"><ArrowLeft className="h-4 w-4" />All parents</Link>
      <PageHeader eyebrow={p.source === "admin_provisioned" ? "Admin-created account" : "Self sign-up"} title={p.name} description={`${p.email}${p.phone ? ` - ${p.phone}` : ""} - email ${p.email_verified ? "verified" : "not verified"} - ${p.status}`}
        actions={<>
          {!p.password_set && <GhostButton onClick={() => act(() => api.post(`/admin/parents/${id}/resend-setup`), "Set-password link re-sent")} data-testid="resend-setup-button"><Mail className="h-4 w-4" />Resend setup link</GhostButton>}
          <GhostButton onClick={() => setAdding(true)} data-testid="admin-add-student-button"><Plus className="h-4 w-4" />Add student</GhostButton>
          <GhostButton onClick={() => act(() => api.patch(`/admin/users/${id}/status`, { status: p.status === "disabled" ? "active" : "disabled" }), "Status updated")} data-testid="toggle-parent-status-button">
            {p.status === "disabled" ? <><CheckCircle2 className="h-4 w-4" />Enable</> : <><Ban className="h-4 w-4" />Disable</>}
          </GhostButton>
        </>} />
      <h2 className="font-display text-lg font-bold mb-3">Students</h2>
      <DataTable testId="parent-students-table" rows={data.students} empty="No students yet." columns={[
        { key: "name", label: "Student", render: (r) => <span className="font-semibold">{r.name}</span> },
        { key: "class_level", label: "Class" },
        { key: "school_name", label: "School", render: (r) => r.school_name || "-" },
        { key: "status", label: "Status", render: (r) => <Pill map={STUDENT_STATUS} value={r.status} /> },
        { key: "actions", label: "", render: (r) => !r.is_paid && <GhostButton onClick={() => setPaying(r)} data-testid={`mark-paid-button-${r.id}`}>Mark paid</GhostButton> },
      ]} />
      <h2 className="font-display text-lg font-bold mt-10 mb-3">Consent records</h2>
      <DataTable testId="parent-consents-table" rows={data.consents} columns={[
        { key: "version_number", label: "Version", render: (r) => `v${r.version_number}` },
        { key: "method", label: "Method", render: (r) => (r.method === "offline_signed_form" ? `Signed form ${r.form_reference || ""}` : `E-sign: "${r.signature_name}"`) },
        { key: "verification_status", label: "Verification", render: (r) => <Pill map={CONSENT_STATUS} value={r.verification_status} /> },
        { key: "revoked", label: "Current", render: (r) => (r.revoked ? "Superseded" : "Yes") },
        { key: "created_at", label: "Recorded", render: (r) => formatDateTime(r.created_at) },
      ]} />
      <h2 className="font-display text-lg font-bold mt-10 mb-3">Payments</h2>
      <DataTable testId="parent-purchases-table" rows={data.purchases} columns={[
        { key: "amount_inr", label: "Amount", render: (r) => `Rs. ${r.amount_inr}` },
        { key: "method", label: "Method", render: (r) => (r.method === "offline_admin" ? `Offline (${r.offline_reference})` : "Razorpay") },
        { key: "state", label: "State", render: (r) => <Pill map={PAYMENT_STATE} value={r.state} /> },
        { key: "created_at", label: "Created", render: (r) => formatDateTime(r.created_at) },
      ]} />
      <MarkPaidDialog student={paying} onClose={() => setPaying(null)} onDone={reload} />
      <AddStudentAdminDialog parentId={id} open={adding} onOpenChange={setAdding} onDone={reload} />
    </div>
  );
}
